import {
   ICadenceStep,
   ICampaignConfiguration,
   IContentReference,
   IContentPool,
   ICriteria,
   IPhoneDefinition,
   IStrategyDefinition,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   IFilterRow,
   IFlowDraft,
   IPlaybook,
   TLeadType,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import type { ICampaignFilterConfig } from '@forwardflow/features/campaign-filter/interfaces'
import { sortTouches } from '@forwardflow/features/campaign-flow/utils/playbook'
import { audienceTypes } from '@forwardflow/features/campaign-flow/utils/describe'
import { toTemplate } from '@forwardflow/features/campaign-flow/utils/text'

/**
 * Draft → backend shapes (campaign v2 handoff, sections 5 and 7).
 *
 * Pure functions with no network; covered by to-configuration.test.ts. The
 * live service calls these right before `createDraft` / `saveConfiguration`
 * and the content library endpoints.
 */

/** Business category ids come from `GET business-categories`; pass the real map in. */
export type TBusinessTypeIds = Record<TLeadType, number>

/** Content references returned by the library when the playbook is created or customized. */
export interface IPlaybookRefs {
   outboundScript: IContentReference
   strategy: IContentReference
   conversation: IContentReference
   /** Required to publish: how the assistant answers when a lead calls back. */
   inboundScript?: IContentReference | null
}

/** One filter row → one v2 criterion. Rows without values are skipped. Same field names as the existing campaign filter. */
export const filterToCriteria = (f: IFilterRow): ICriteria | null =>
   f.value.length
      ? { field: f.field, operator: f.operator, value: f.value, logic: 'AND' }
      : null

/** The same audience in the existing campaign-filter shape (`ICampaignFilterConfig`), for the current backend. */
export const toFilterConfig = (draft: IFlowDraft): ICampaignFilterConfig => ({
   campaign_type: draft.audience.kinds,
   lead_type_filter: draft.audience.leadTypes,
   /* The dashboard's activity triggers, when the campaign reacts to activity. */
   activity_campaign_filter_criteria:
      draft.audience.kinds.includes('activity') && draft.audience.activity
         ? {
              events: draft.audience.activity.events.length
                 ? draft.audience.activity.events
                 : ['all'],
              lookback_days: draft.audience.activity.lookbackDays,
              triggers: draft.audience.activity.pondIds.map((id) => ({
                 type: 'pond' as const,
                 value: Number(id),
              })),
           }
         : null,
   campaign_filters: {
      eligible_criteria: draft.audience.filters.filter((f) => f.value.length),
   },
   first_to_contact: { buffer_time: 0 },
})

export const toCadence = (pb: Pick<IPlaybook, 'touches'>): ICadenceStep[] =>
   sortTouches(pb.touches).map((t, i) => ({
      id: t.id,
      order: i + 1,
      day: t.day,
      localTime: t.time,
      action: t.action === 'call' ? 'call' : 'sms',
      ...(t.action === 'call'
         ? {
              voicemail: t.voicemail
                 ? ('if-configured' as const)
                 : ('none' as const),
           }
         : {}),
   }))

/** Texts and voicemails as one outbound content group (a "strategy" to the backend). Names are generated. */
export const toStrategyDefinition = (
   pb: IPlaybook,
   businessTypeId: number,
   campaignName: string
): IStrategyDefinition => ({
   name: `${campaignName} - Outbound content group 1`,
   businessTypeId,
   sms: pb.texts.map((m) => ({ template: toTemplate(m.text) })),
   email: [],
   voicemail: pb.voicemails.map((m) => ({ template: toTemplate(m.text) })),
})

/** The call script as an outbound phone definition; the first section's text is the opener. */
export const toPhoneDefinition = (
   pb: IPlaybook,
   businessTypeId: number,
   campaignName: string
): IPhoneDefinition => ({
   name: `${campaignName} - Call script`,
   businessTypeId,
   callType: 'outbound',
   callDirections: ['outbound'],
   leadTypes: ['known'],
   callerTypes: ['ai'],
   firstMessage: toTemplate(pb.script[0]?.text ?? ''),
   sections: pb.script.map((s, i) => ({
      name: s.title,
      type: 'call_outbound',
      order: i + 1,
      content: toTemplate(s.text),
   })),
})

/** Content references per lead type; a type without its own uses the first type's. */
export type TPlaybookRefsByType = Partial<Record<TLeadType, IPlaybookRefs>>

export const toConfiguration = (
   draft: IFlowDraft,
   ids: TBusinessTypeIds,
   refs: TPlaybookRefsByType | null
): ICampaignConfiguration => {
   const types = audienceTypes(draft.audience)
   const businessTypeIds = types.map((t) => ids[t])
   /*
    * One content pool per lead type, each with its own script, outbound content group
    * and reply instructions. The first lead type's pool is the default.
    */
   const pool = (t: TLeadType, i: number): IContentPool => {
      const r = refs?.[t] ?? refs?.[types[0]] ?? null
      return {
         id: i === 0 ? 'pool-default' : `pool-${t}`,
         businessTypeId: ids[t],
         isDefault: i === 0,
         outboundScripts: r ? [r.outboundScript] : [],
         inboundScripts: r?.inboundScript ? [r.inboundScript] : [],
         strategies: r ? [r.strategy] : [],
         conversationContent: r ? [r.conversation] : [],
      }
   }
   const criteria = draft.audience.filters
      .map(filterToCriteria)
      .filter((c): c is ICriteria => !!c)
   return {
      schemaVersion: 2,
      businessTypeIds,
      source: { mode: 'dynamic', criteria },
      routing: {
         enabled: true,
         strategy:
            draft.handoff.mode === 'first_to_answer'
               ? 'jump_ball'
               : 'round_robin',
         agentIds: draft.handoff.agentIds,
      },
      phoneNumbers: draft.numbers.selected.map((n) => toE164(n.number)),
      contentPools: types.map(pool),
      subtypes: [],
      cadence: toCadence(draft.playbook),
      selectionPolicy: {
         phone: 'round_robin',
         strategy: 'ordered_journey_v1',
         exhaustion: 'repeat-last',
      },
   }
}

/** "(719) 555-0140" → "+17195550140" (US numbers). */
export const toE164 = (display: string) => {
   const digits = display.replace(/\D/g, '')
   return digits.length === 10 ? `+1${digits}` : `+${digits}`
}
